import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Paperclip, Calculator, X, Wand2, FileText, AlertTriangle, CheckCircle2, Printer } from 'lucide-react';
import api from '../../../api';
import CircuitSchedulePdfPreview from '../../../components/CircuitSchedulePdfPreview';
import BottomSheet from '../../../components/ui/BottomSheet';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { FormLabel } from '../../../components/ui/FormLabel';

// Combobox component for inputs with predefined options and custom input support
const ComboInput = ({ value, onChange, options = [], placeholder, className, onContextMenu, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setFilterText(''); // Reset filter when closing
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    if (disabled) return;
    onChange(option);
    setIsOpen(false);
    setFilterText(''); // Reset filter after selection
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleFocus = () => {
    if (disabled) return;
    if (options.length > 0) {
      setIsOpen(true);
      setFilterText(''); // Reset filter when focusing - show all options
    }
  };

  const handleChange = (e) => {
    if (disabled) return;
    const newValue = e.target.value;
    onChange(newValue);
    // Update filter text based on what user is typing
    setFilterText(newValue);
    // Keep dropdown open while typing
    if (!isOpen && options.length > 0) {
      setIsOpen(true);
    }
  };

  // Filter options based on what user is typing (filterText), not the current value
  // When filterText is empty (on focus), show all options
  const filteredOptions = filterText
    ? options.filter(opt => opt.toString().toLowerCase().includes(filterText.toLowerCase()))
    : options;

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        onContextMenu={onContextMenu}
        disabled={disabled}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-[100] mt-1 left-0 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto min-w-[28rem] w-auto">
          {filteredOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => handleSelect(option)}
              className="w-full px-3 py-2 text-left text-xs text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none whitespace-nowrap"
              type="button"
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// --- Max Zs lookup helpers (BS 7671) ---
// Alias different device standards to the same 60898 lookup table
const BSEN_ALIASES = {
  '60898': '60898',
  '61009': '60898',
  '61009/62606 AFDD': '60898',
};

// Normalize BS(EN) input to canonical key
function normalizeBsEn(input) {
  if (!input) return '';
  const raw = input.toString().trim().toUpperCase();
  if (BSEN_ALIASES[raw]) return BSEN_ALIASES[raw];
  const digits = raw.replace(/[^0-9]/g, '');
  if (digits === '60898' || digits === '61009') return '60898';
  if (raw.includes('61009') && raw.includes('62606')) return '60898';
  return '';
}

// Map times to known tables: 0.2/0.4/1 -> 0.4, 5 -> 5
function normalizeTime(t) {
  const s = (t ?? '').toString().trim();
  if (s === '5') return '5';
  return '0.4';
}

// Example table for BS 60898 @ 230V (approximate; extend/adjust as needed)
const MAX_ZS_TABLES = {
  '60898': {
    '0.4': {
      B: { '6': 7.29, '10': 4.37, '16': 2.73, '20': 2.19, '25': 1.75, '32': 1.37, '40': 1.09, '50': 0.87, '63': 0.69 },
      C: { '6': 3.64, '10': 2.19, '16': 1.37, '20': 1.09, '25': 0.87, '32': 0.69, '40': 0.55, '50': 0.44, '63': 0.35 },
      D: { '6': 2.43, '10': 1.46, '16': 0.91, '20': 0.73, '25': 0.58, '32': 0.46, '40': 0.36, '50': 0.29, '63': 0.23 },
    },
    '5': {
      B: { '6': 9.58, '10': 5.75, '16': 3.65, '20': 2.92, '25': 2.33, '32': 1.83, '40': 1.46, '50': 1.17, '63': 0.93 },
      C: { '6': 4.79, '10': 2.88, '16': 1.83, '20': 1.46, '25': 1.17, '32': 0.91, '40': 0.73, '50': 0.58, '63': 0.47 },
      D: { '6': 3.19, '10': 1.92, '16': 1.22, '20': 0.97, '25': 0.78, '32': 0.61, '40': 0.49, '50': 0.39, '63': 0.31 },
    },
  },
};

function computeMaxZsBase({ time, bsEn, type, rating }) {
  const stdKey = normalizeBsEn(bsEn);
  if (!stdKey) return '';
  const tKey = normalizeTime(time);
  const std = MAX_ZS_TABLES[stdKey];
  if (!std) return '';
  const timeTable = std[tKey];
  if (!timeTable) return '';
  const typeKey = (type || '').toString().toUpperCase();
  const typeTable = timeTable[typeKey];
  if (!typeTable) return '';
  const zs = typeTable[(rating || '').toString()];
  return zs != null ? zs : '';
}

function applyZsFactor(zsValue, factor) {
  if (zsValue === '' || zsValue == null) return '';
  const num = Number(zsValue);
  if (Number.isNaN(num)) return '';
  const scaled = num * factor;
  // keep 2 decimal places for readability
  return parseFloat(scaled.toFixed(2)).toString();
}

const CircuitsManager = ({ boardData, onSave, onBack, maxCircuits = null }) => {
  const navigate = useNavigate();
  const [circuits, setCircuits] = useState(boardData?.circuits || []);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [zsFactor, setZsFactor] = useState(1.0); // 1.0 => 100%, 0.8 => 80%
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, circuitId: null, field: null });
  const [notesModal, setNotesModal] = useState({ open: false, circuitId: null, notes: '' });
  const [selectedColumn, setSelectedColumn] = useState(null); // Selected column field name
  const [fillColumnModal, setFillColumnModal] = useState({ open: false, field: null, value: '' });
  const [isCompleted, setIsCompleted] = useState(boardData?.isCompleted || false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const [boardNotes, setBoardNotes] = useState(boardData?.notes || '');

  // Sync notes when boardData changes
  useEffect(() => {
    setBoardNotes(boardData?.notes || '');
  }, [boardData?.notes]);

  // Column widths for easy adjustment (31 columns total). Use any valid CSS width (e.g., '60px', '8rem').
  // These widths are enforced in desktop mode with horizontal scrolling enabled.
  const columnWidths = [
    '2.25rem', // Checkbox
    '2.5rem', // #
    '15rem',   // Circuit description
    '2.5rem',    // No. points served
    '2.5rem',   // Wiring type
    '2.5rem',   // Ref method
    '3.5rem',    // Live
    '3.5rem',    // cpc
    '3.5rem',    // Max disconnect time
    '5rem',    // BS (EN)
    '3.5rem',    // Type
    '3.5rem',    // Rating
    '3.5rem',    // Breaking capacity
    '5rem',    // Max Zs
    '5rem',    // RCD BS(EN)
    '3.5rem',    // RCD Type
    '3.5rem',   // RCD Operating current
    '3.5rem',    // RCD Rating
    '3rem',    // r1 (live)
    '3rem',    // m (neutral)
    '3rem',    // r2 (cpc)
    '5rem',    // R1 + R2
    '3rem',    // R2
    '3rem',    // Test voltage
    '4rem',    // L-L
    '4rem',    // L-E
    '3rem',    // Polarity
    '6.25rem',    // Measured Zs
    '3.5rem',    // Discon. time
    '3rem',    // Test button
    '3rem',    // AFDD Test button
    '3rem',    // Notes
  ];

  // Font size controls for this table (edit here to change globally for this table)
  const headerTextSize = 'text-[8pt]'; // 10pt font size for table headers
  const cellTextSize = 'text-xs';   // 12px - complies with typography constraint
  const baseInputClass = `w-full px-0 py-0 border-0 bg-transparent text-gray-900 dark:text-gray-100 ${cellTextSize} focus:outline-none`;

  useEffect(() => {
    const closeOnClick = () => setContextMenu(prev => ({ ...prev, open: false }));
    if (contextMenu.open) {
      document.addEventListener('click', closeOnClick);
      return () => document.removeEventListener('click', closeOnClick);
    }
  }, [contextMenu.open]);

  const openContextMenu = (e, circuitId, field) => {
    e.preventDefault();
    setContextMenu({ open: true, x: e.clientX, y: e.clientY, circuitId, field });
  };

  const applyToCell = (value) => {
    if (!contextMenu.circuitId || !contextMenu.field) return;
    handleCircuitChange(contextMenu.circuitId, contextMenu.field, value);
    setContextMenu(prev => ({ ...prev, open: false }));
  };

  const clearCell = () => applyToCell('');

  const autofillRow = (circuitId, value) => {
    setCircuits(prev => prev.map(c => {
      if (c.id !== circuitId) return c;
      const next = { ...c };
      Object.keys(next).forEach(k => {
        if (k === 'id') return;
        if (typeof next[k] === 'string') next[k] = value;
      });
      return next;
    }));
    setHasUnsavedChanges(true);
    setContextMenu(prev => ({ ...prev, open: false }));
  };

  // Warn user if they try to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }
    // Save circuits back to board
    if (onSave && circuits.length > 0) {
      onSave(circuits);
    }
    onBack();
  };

  const handleAddCircuit = () => {
    // Check if we've reached the maximum number of circuits
    if (maxCircuits !== null && circuits.length >= maxCircuits) {
      alert(`Maximum of ${maxCircuits} circuits allowed.`);
      return;
    }
    const newCircuit = {
      id: crypto.randomUUID(),
      displayNumber: (circuits.length + 1).toString(),
      description: '',
      numPointsServed: '',
      wiringType: '',
      refMethod: '',
      live: '',
      cpc: '',
      maxDisconnectTime: '',
      bsEn: '',
      type: '',
      rating: '',
      breakingCapacity: '',
      maxZs: '',
        rcdBsEn: '',
      rcdType: '',
      rcdOperatingCurrent: '',
      rcdRating: '',
      r1Live: '',
      mNeutral: '',
      r2Cpc: '',
      r1PlusR2: '',
      r2: '',
      testVoltage: '',
      ll: '',
      le: '',
      polarity: '',
      measuredZs: '',
      rcdDisconTime: '',
      rcdTestButton: '',
      afddTestButton: '',
      notes: '',
        selected: false,
    };
    setCircuits(prev => [...prev, newCircuit]);
    setHasUnsavedChanges(true);
  };

  const makeNewCircuit = (num = '') => ({
    id: crypto.randomUUID(),
    displayNumber: num.toString(),
    description: '',
    numPointsServed: '',
    wiringType: '',
    refMethod: '',
    live: '',
    cpc: '',
    maxDisconnectTime: '',
    bsEn: '',
    type: '',
    rating: '',
    breakingCapacity: '',
    maxZs: '',
    rcdBsEn: '',
    rcdType: '',
    rcdOperatingCurrent: '',
    rcdRating: '',
    r1Live: '',
    mNeutral: '',
    r2Cpc: '',
    r1PlusR2: '',
    r2: '',
    testVoltage: '',
    ll: '',
    le: '',
    polarity: '',
    measuredZs: '',
    rcdDisconTime: '',
    rcdTestButton: '',
    afddTestButton: '',
    notes: '',
    selected: false,
  });

  const getSelectedIndexes = () => circuits.map((c, i) => c.selected ? i : -1).filter(i => i !== -1);

  const moveSelectedUp = () => {
    const idxs = getSelectedIndexes();
    if (idxs.length === 0) return;
    const i = idxs[0];
    if (i <= 0) return;
    setCircuits(prev => {
      const next = [...prev];
      const tmp = next[i - 1];
      next[i - 1] = next[i];
      next[i] = tmp;
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const moveSelectedDown = () => {
    const idxs = getSelectedIndexes();
    if (idxs.length === 0) return;
    const i = idxs[idxs.length - 1];
    if (i >= circuits.length - 1) return;
    setCircuits(prev => {
      const next = [...prev];
      const tmp = next[i + 1];
      next[i + 1] = next[i];
      next[i] = tmp;
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const addAboveSelected = () => {
    if (maxCircuits !== null && circuits.length >= maxCircuits) {
      alert(`Maximum of ${maxCircuits} circuits allowed.`);
      return;
    }
    const idxs = getSelectedIndexes();
    if (idxs.length === 0) return;
    const i = idxs[0];
    setCircuits(prev => {
      const next = [...prev];
      next.splice(i, 0, makeNewCircuit(''));
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const addBelowSelected = () => {
    if (maxCircuits !== null && circuits.length >= maxCircuits) {
      alert(`Maximum of ${maxCircuits} circuits allowed.`);
      return;
    }
    const idxs = getSelectedIndexes();
    if (idxs.length === 0) return;
    const i = idxs[idxs.length - 1];
    setCircuits(prev => {
      const next = [...prev];
      next.splice(i + 1, 0, makeNewCircuit(''));
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const clearSelectedRows = () => {
    setCircuits(prev => prev.map(c => {
      if (!c.selected) return c;
      const copy = { ...c };
      Object.keys(copy).forEach(k => {
        if (k === 'id' || k === 'selected') return;
        if (typeof copy[k] === 'string') copy[k] = '';
      });
      return copy;
    }));
    setHasUnsavedChanges(true);
  };

  const deleteSelectedRows = () => {
    setCircuits(prev => prev.filter(c => !c.selected));
    setHasUnsavedChanges(true);
  };

  const handleCircuitChange = (circuitId, field, value) => {
    setCircuits(prev => prev.map(circuit => {
      if (circuit.id !== circuitId) return circuit;
      const next = { ...circuit, [field]: value };

      // If user enters "N/A" in r1, r2, or rn fields, set all three to "N/A"
      const valueStr = (value || '').toString().trim().toUpperCase();
      const rFields = ['r1Live', 'mNeutral', 'r2Cpc']; // r1, rn (mNeutral), r2
      if (valueStr === 'N/A' && rFields.includes(field)) {
        rFields.forEach(f => {
          if (f !== field) {
            next[f] = 'N/A';
          }
        });
      }

      // If user enters a numeric value in R1+R2, automatically set R2 to "N/A"
      if (field === 'r1PlusR2') {
        const r1PlusR2Value = (value || '').toString().trim();
        // Check if it's a valid numeric value (not empty, not N/A, not other text)
        if (r1PlusR2Value && r1PlusR2Value.toUpperCase() !== 'N/A' && !isNaN(parseFloat(r1PlusR2Value)) && isFinite(parseFloat(r1PlusR2Value))) {
          next.r2 = 'N/A';
        }
      }

      // If user selects anything but "62606" in RCD BS(EN) (from the options list), set AFDD Test Button to "N/A"
      if (field === 'rcdBsEn') {
        const rcdBsEnOptions = ['61008', '61009', '62606', '4293', '1363', '7071', '7288', 'LIM', 'N/A'];
        const rcdBsEnValue = (value || '').toString().trim();
        // Only auto-set if the value is in the options list (not a custom typed value)
        if (rcdBsEnValue && rcdBsEnOptions.includes(rcdBsEnValue) && rcdBsEnValue !== '62606') {
          next.afddTestButton = 'N/A';
        }
      }

      // If BS(EN) represents RCBO/AFDD (61009 family), mirror to RCD fields
      const isRcboAfdd = (val) => {
        const v = (val || '').toString().toUpperCase();
        return v === '61009' || v === '61009/62606 AFDD';
      };

      // When rating changes for 61009/61009/62606, mirror to rcdRating and rcdBsEn
      if (field === 'rating' && isRcboAfdd(next.bsEn)) {
        next.rcdRating = (value || '').toString();
        next.rcdBsEn = next.bsEn || next.rcdBsEn;
      }

      // When bsEn changes into a 61009 variant, mirror bsEn into rcdBsEn and copy rating into rcdRating if present
      if (field === 'bsEn' && isRcboAfdd(value)) {
        next.rcdBsEn = (value || '').toString();
        if (next.rating) {
          next.rcdRating = next.rating.toString();
        }
      }

      // Auto-calc Max Zs when any of the inputs change and field not directly editing maxZs
      if (['maxDisconnectTime', 'bsEn', 'type', 'rating'].includes(field)) {
        const base = computeMaxZsBase({
          time: field === 'maxDisconnectTime' ? value : next.maxDisconnectTime,
          bsEn: field === 'bsEn' ? value : next.bsEn,
          type: field === 'type' ? value : next.type,
          rating: field === 'rating' ? value : next.rating,
        });
        const scaled = applyZsFactor(base, zsFactor);
        if (scaled) next.maxZs = scaled;
      }

      return next;
    }));
    setHasUnsavedChanges(true);
  };

  const recalcAllMaxZs = (factor) => {
    setCircuits(prev => prev.map(c => {
      const base = computeMaxZsBase({
        time: c.maxDisconnectTime,
        bsEn: c.bsEn,
        type: c.type,
        rating: c.rating,
      });
      const scaled = applyZsFactor(base, factor);
      return scaled ? { ...c, maxZs: scaled } : c;
    }));
    setHasUnsavedChanges(true);
  };

  const handleDeleteCircuit = (circuitId) => {
    setCircuits(prev => prev.filter(circuit => circuit.id !== circuitId));
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(circuits, { isCompleted, notes: boardNotes });
    }
    setHasUnsavedChanges(false);
    // Show success message or toast
    alert('Circuits saved successfully!');
  };

  const handleOpenNotesDrawer = () => {
    setNotesDrawerOpen(true);
  };

  const handleCloseNotesDrawer = () => {
    setNotesDrawerOpen(false);
  };

  const handleNotesChange = (value) => {
    setBoardNotes(value);
    setHasUnsavedChanges(true);
  };

  const handleSaveNotes = () => {
    // Notes are already saved in state, just close the drawer
    // The notes will be saved when user clicks "Save Changes"
    setNotesDrawerOpen(false);
  };

  const handleMarkAsDone = () => {
    const newStatus = !isCompleted;
    setIsCompleted(newStatus);
    setHasUnsavedChanges(true);
    
    // Update boardData with new status
    if (onSave) {
      onSave(circuits, { isCompleted: newStatus });
    }
  };

  const handlePrintSchedule = async () => {
    try {
      setGeneratingPdf(true);
      
      // Prepare data for PDF generation
      const scheduleData = {
        board: boardData,
        circuits: circuits,
        client: boardData?.client || {},
        installationAddress: boardData?.installationAddress || '',
      };

      // Generate PDF via API
      const response = await api.post('/certificates/circuit-schedule/pdf', scheduleData, {
        responseType: 'blob', // Important for PDF binary data
      });

      // Create blob URL for preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      setPdfUrl(url);
      setPdfPreviewOpen(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    // Clean up blob URL when closing
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const openNotesModal = (circuitId) => {
    const circuit = circuits.find(c => c.id === circuitId);
    setNotesModal({ open: true, circuitId, notes: circuit?.notes || '' });
  };

  const closeNotesModal = () => {
    setNotesModal({ open: false, circuitId: null, notes: '' });
  };

  const saveNotes = () => {
    handleCircuitChange(notesModal.circuitId, 'notes', notesModal.notes);
    closeNotesModal();
  };

  const handleColumnClick = (field) => {
    setSelectedColumn(selectedColumn === field ? null : field);
  };

  const openFillColumnModal = () => {
    if (selectedColumn) {
      setFillColumnModal({ open: true, field: selectedColumn, value: '' });
    }
  };

  const closeFillColumnModal = () => {
    setFillColumnModal({ open: false, field: null, value: '' });
  };

  const fillColumn = (fillBlanksOnly = false) => {
    if (!fillColumnModal.field) return;
    
    setCircuits(prev => prev.map(circuit => {
      if (fillBlanksOnly && circuit[fillColumnModal.field]) {
        return circuit;
      }
      return {
        ...circuit,
        [fillColumnModal.field]: fillColumnModal.value
      };
    }));
    setHasUnsavedChanges(true);
    closeFillColumnModal();
    setSelectedColumn(null);
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Header */}
      <div className="bg-[#141821] border-b border-gray-200 dark:border-gray-700 flex-shrink-0 z-10">
        <div className="max-w-[99vw] mx-auto px-4 py-4 bg-[#141821]">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={handleBack}
              variant="outline"
              size="sm"
              className="inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {boardData?.name || 'Board Circuits'}
            </h1>
            <span className={`px-3 py-1 text-white text-xs font-semibold rounded ${
              isCompleted ? 'bg-green-500' : 'bg-orange-500'
            }`}>
              {isCompleted ? 'COMPLETED' : 'IN PROGRESS'}
            </span>
          </div>

          {/* Action Buttons */}
          {!circuits.some(c => c.selected) && (
          <div className="flex flex-wrap gap-2">
            <Button
              disabled
              variant="outline"
              size="sm"
            >
              Quick add
            </Button>
            <Button
              onClick={handleAddCircuit}
              disabled={isCompleted || (maxCircuits !== null && circuits.length >= maxCircuits)}
              variant="outline"
              size="sm"
              title={maxCircuits !== null && circuits.length >= maxCircuits ? `Maximum of ${maxCircuits} circuits allowed` : 'Add circuit'}
            >
              Add
            </Button>
            <Button
              disabled
              variant="outline"
              size="sm"
            >
              Check
            </Button>
            <Button
              onClick={openFillColumnModal}
              disabled={!selectedColumn || isCompleted}
              variant="outline"
              size="sm"
            >
              Fill column
            </Button>
            <Button
              onClick={() => {
                const nextFactor = zsFactor === 1.0 ? 0.8 : 1.0;
                const labelFrom = zsFactor === 1.0 ? '100%' : '80%';
                const labelTo = nextFactor === 1.0 ? '100%' : '80%';
                const confirmed = window.confirm(`Change Max Zs factor from ${labelFrom} to ${labelTo}? This will recalculate all Max Zs values.`);
                if (!confirmed) return;
                setZsFactor(nextFactor);
                recalcAllMaxZs(nextFactor);
              }}
              disabled={isCompleted}
              variant="outline"
              size="sm"
            >
              {zsFactor === 1.0 ? '100% Max Zs' : '80% Max Zs'}
            </Button>
            <span className="px-3 py-2 text-xs font-medium text-gray-700 dark:text-gray-300">
              {circuits.length} circuits
            </span>
            <Button
              onClick={handleMarkAsDone}
              variant="outline"
              size="sm"
              className="inline-flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
              {isCompleted ? 'Mark as in progress' : 'Mark as done'}
            </Button>
            <Button
              onClick={handlePrintSchedule}
              disabled={generatingPdf || circuits.length === 0}
              variant="outline"
              size="sm"
              className="inline-flex items-center gap-2"
            >
              {generatingPdf ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4" />
                  Print schedule
                </>
              )}
            </Button>
          </div>
          )}

          {/* Selection Toolbar */}
          {circuits.some(c => c.selected) && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button onClick={moveSelectedUp} disabled={isCompleted} variant="outline" size="sm">Move up</Button>
              <Button onClick={moveSelectedDown} disabled={isCompleted} variant="outline" size="sm">Move down</Button>
              <Button onClick={addAboveSelected} disabled={isCompleted} variant="outline" size="sm">Add above</Button>
              <Button onClick={addBelowSelected} disabled={isCompleted} variant="outline" size="sm">Add below</Button>
              <Button onClick={clearSelectedRows} disabled={isCompleted} variant="outline" size="sm">Clear row</Button>
              <Button 
                onClick={deleteSelectedRows} 
                disabled={isCompleted} 
                variant="outline" 
                size="sm"
                className="text-red-700 dark:text-red-300 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-gray-600"
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content with Horizontal Scroll */}
      <div className="flex-1 overflow-hidden pb-20 bg-[#141821]" style={{ minHeight: 0 }}>
        <div className="h-full overflow-x-auto overflow-y-visible relative">
          {isCompleted && (
            <div className="absolute inset-0 z-50 bg-white/30 dark:bg-gray-900/30 backdrop-blur-[1px] flex items-center justify-center">
              <div className="bg-white/95 dark:bg-gray-800/95 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg p-6 max-w-md mx-4 backdrop-blur-sm">
                <p className="text-gray-900 dark:text-gray-100 text-center text-xs font-medium">
                  This board has been marked as completed and cannot be edited. You need to mark it as 'In progress' to make changes.
                </p>
              </div>
            </div>
          )}
          <div className="bg-white dark:bg-gray-800" style={{ minWidth: 'max-content' }}>
          <table className="border-collapse" style={{ width: 'max-content' }}>
            <colgroup>
              {columnWidths.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <thead className="bg-gray-50 dark:bg-gray-900">
              {/* Primary Header Row */}
              <tr>
                <th rowSpan="2" className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20`}>
                  <input type="checkbox" />
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('displayNumber')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'displayNumber' ? 'ring-2 ring-blue-500' : ''}`}>#</th>
                <th rowSpan="2" onClick={() => handleColumnClick('description')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'description' ? 'ring-2 ring-blue-500' : ''}`}>
                  Circuit description
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('numPointsServed')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'numPointsServed' ? 'ring-2 ring-blue-500' : ''}`}>
                  No. points served
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('wiringType')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'wiringType' ? 'ring-2 ring-blue-500' : ''}`}>
                  Wiring type
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('refMethod')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'refMethod' ? 'ring-2 ring-blue-500' : ''}`}>
                  Ref method
                </th>
                <th colSpan="2" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20`}>
                  CONDUCTORS
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('maxDisconnectTime')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 ${selectedColumn === 'maxDisconnectTime' ? 'ring-2 ring-blue-500' : ''}`}>
                  Max disconnect time
                </th>
                <th colSpan="4" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20`}>
                  OVERCURRENT DEVICES
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('maxZs')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 ${selectedColumn === 'maxZs' ? 'ring-2 ring-blue-500' : ''}`}>
                  Max Zs
                </th>
                <th colSpan="3" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20`}>
                  RCD
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('rcdRating')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 ${selectedColumn === 'rcdRating' ? 'ring-2 ring-blue-500' : ''}`}>
                  Rating
                </th>
                <th colSpan="3" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20`}>
                  RING FINAL CIRCUITS
                </th>
                <th colSpan="2" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-indigo-50 dark:bg-indigo-900/20`}>
                  R1 + R2 OR R2
                </th>
                <th colSpan="4" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-pink-50 dark:bg-pink-900/20`}>
                  INSULATION RESISTANCE
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('measuredZs')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-pink-50 dark:bg-pink-900/20 ${selectedColumn === 'measuredZs' ? 'ring-2 ring-blue-500' : ''}`}>
                  Measured Zs
                </th>
                <th colSpan="2" className={`border border-gray-300 dark:border-gray-700 border-b-0 px-2 py-2 text-center align-middle ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20`}>
                  RCD
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('afddTestButton')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 ${selectedColumn === 'afddTestButton' ? 'ring-2 ring-blue-500' : ''}`}>
                  AFDD Test button
                </th>
                <th rowSpan="2" onClick={() => handleColumnClick('notes')} className={`border border-gray-300 dark:border-gray-700 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-100 dark:bg-blue-900/30 ${selectedColumn === 'notes' ? 'ring-2 ring-blue-500' : ''}`}>
                  Notes
                </th>
              </tr>
              {/* Secondary Header Row */}
              <tr>
                <th onClick={() => handleColumnClick('live')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'live' ? 'ring-2 ring-blue-500' : ''}`}>
                  Live
                </th>
                <th onClick={() => handleColumnClick('cpc')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/20 ${selectedColumn === 'cpc' ? 'ring-2 ring-blue-500' : ''}`}>
                  cpc
                </th>
                <th onClick={() => handleColumnClick('bsEn')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 ${selectedColumn === 'bsEn' ? 'ring-2 ring-blue-500' : ''}`}>
                  BS (EN)
                </th>
                <th onClick={() => handleColumnClick('type')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 ${selectedColumn === 'type' ? 'ring-2 ring-blue-500' : ''}`}>
                  Type
                </th>
                <th onClick={() => handleColumnClick('rating')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 ${selectedColumn === 'rating' ? 'ring-2 ring-blue-500' : ''}`}>
                  Rating
                </th>
                <th onClick={() => handleColumnClick('breakingCapacity')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-yellow-50 dark:bg-yellow-900/20 ${selectedColumn === 'breakingCapacity' ? 'ring-2 ring-blue-500' : ''}`}>
                  Breaking capacity
                </th>
                <th onClick={() => handleColumnClick('rcdBsEn')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 ${selectedColumn === 'rcdBsEn' ? 'ring-2 ring-blue-500' : ''}`}>
                  BS(EN)
                </th>
                <th onClick={() => handleColumnClick('rcdType')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 ${selectedColumn === 'rcdType' ? 'ring-2 ring-blue-500' : ''}`}>
                  Type
                </th>
                <th onClick={() => handleColumnClick('rcdOperatingCurrent')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 ${selectedColumn === 'rcdOperatingCurrent' ? 'ring-2 ring-blue-500' : ''}`}>
                  Operating current
                </th>
                <th onClick={() => handleColumnClick('r1Live')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 ${selectedColumn === 'r1Live' ? 'ring-2 ring-blue-500' : ''}`}>
                  r1 (live)
                </th>
                <th onClick={() => handleColumnClick('mNeutral')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 ${selectedColumn === 'mNeutral' ? 'ring-2 ring-blue-500' : ''}`}>
                  m (neutral)
                </th>
                <th onClick={() => handleColumnClick('r2Cpc')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 ${selectedColumn === 'r2Cpc' ? 'ring-2 ring-blue-500' : ''}`}>
                  r2 (cpc)
                </th>
                <th onClick={() => handleColumnClick('r1PlusR2')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-indigo-50 dark:bg-indigo-900/20 ${selectedColumn === 'r1PlusR2' ? 'ring-2 ring-blue-500' : ''}`}>
                  R1 + R2
                </th>
                <th onClick={() => handleColumnClick('r2')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-indigo-50 dark:bg-indigo-900/20 ${selectedColumn === 'r2' ? 'ring-2 ring-blue-500' : ''}`}>
                  R2
                </th>
                <th onClick={() => handleColumnClick('testVoltage')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-pink-50 dark:bg-pink-900/20 ${selectedColumn === 'testVoltage' ? 'ring-2 ring-blue-500' : ''}`}>
                  Test voltage
                </th>
                <th onClick={() => handleColumnClick('ll')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-pink-50 dark:bg-pink-900/20 ${selectedColumn === 'll' ? 'ring-2 ring-blue-500' : ''}`}>
                  L-L
                </th>
                <th onClick={() => handleColumnClick('le')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-pink-50 dark:bg-pink-900/20 ${selectedColumn === 'le' ? 'ring-2 ring-blue-500' : ''}`}>
                  L-E
                </th>
                <th onClick={() => handleColumnClick('polarity')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-pink-50 dark:bg-pink-900/20 ${selectedColumn === 'polarity' ? 'ring-2 ring-blue-500' : ''}`}>
                  Polarity
                </th>
                <th onClick={() => handleColumnClick('rcdDisconTime')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 ${selectedColumn === 'rcdDisconTime' ? 'ring-2 ring-blue-500' : ''}`}>
                  Discon. time
                </th>
                <th onClick={() => handleColumnClick('rcdTestButton')} className={`border border-gray-300 dark:border-gray-700 border-t-0 px-2 py-2 text-center align-bottom cursor-pointer ${headerTextSize} font-semibold text-gray-700 dark:text-gray-300 bg-green-50 dark:bg-green-900/20 ${selectedColumn === 'rcdTestButton' ? 'ring-2 ring-blue-500' : ''}`}>
                  Test button
                </th>
              </tr>
            </thead>
            <tbody>
              {circuits.length === 0 ? (
                <tr>
                  <td colSpan="32" className="border border-gray-300 dark:border-gray-700 px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No circuits added yet. Click "Add" or "Quick add" to create a circuit.
                  </td>
                </tr>
              ) : (
                circuits.map((circuit, index) => (
                  <CircuitRow
                    key={circuit.id}
                    index={index}
                    circuit={circuit}
                    onChange={handleCircuitChange}
                    onDelete={handleDeleteCircuit}
                    zsFactor={zsFactor}
                    onOpenContextMenu={openContextMenu}
                    inputClassName={baseInputClass}
                    onOpenNotesModal={openNotesModal}
                    selectedColumn={selectedColumn}
                    boardData={boardData}
                    isCompleted={isCompleted}
                  />
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.open && (
        <div
          className="fixed z-[200] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg"
          style={{ left: contextMenu.x + 4, top: contextMenu.y + 4, minWidth: 220 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex gap-2">
            {['LIM', 'N/A', 'N/V', '---'].map(val => (
              <Button
                key={val}
                variant="outline"
                size="sm"
                className="px-2 py-1 text-xs"
                onClick={() => applyToCell(val)}
              >
                {val}
              </Button>
            ))}
          </div>
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50 dark:hover:bg-gray-700"
            onClick={clearCell}
          >
            <X className="w-4 h-4" />
            Clear cell
          </button>
          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => autofillRow(contextMenu.circuitId, '---')}
          >
            <Wand2 className="w-4 h-4" />
            Autofill row ---
          </button>
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => autofillRow(contextMenu.circuitId, 'N/A')}
          >
            <Wand2 className="w-4 h-4" />
            Autofill row N/A
          </button>
        </div>
      )}

      {/* Notes Modal */}
      {notesModal.open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl m-4">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Circuit Notes</h2>
              <button
                onClick={closeNotesModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <Textarea
                value={notesModal.notes}
                onChange={(e) => setNotesModal({ ...notesModal, notes: e.target.value })}
                className="text-xs min-h-[200px] resize-y border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Add notes for this circuit..."
              />
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3">
              <Button
                onClick={closeNotesModal}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={saveNotes}
                variant="primary"
                size="sm"
              >
                Save Notes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fill Column Modal */}
      {fillColumnModal.open && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md m-4">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Fill Column</h2>
              <button
                onClick={closeFillColumnModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <FormLabel>
                Set column value to...
              </FormLabel>
              <Input
                type="text"
                value={fillColumnModal.value}
                onChange={(e) => setFillColumnModal({ ...fillColumnModal, value: e.target.value })}
                className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter value to fill..."
                autoFocus
              />
              <div className="mt-3 flex gap-2">
                {['N/A', 'N/V', 'LIM', '---'].map((value) => (
                  <Button
                    key={value}
                    type="button"
                    onClick={() => setFillColumnModal({ ...fillColumnModal, value })}
                    variant="outline"
                    size="sm"
                  >
                    {value}
                  </Button>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end gap-3">
              <Button
                onClick={closeFillColumnModal}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={() => fillColumn(true)}
                variant="outline"
                size="sm"
              >
                Fill blanks
              </Button>
              <Button
                onClick={() => fillColumn(false)}
                variant="primary"
                size="sm"
              >
                Fill all
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Slide-over */}
      <CircuitSchedulePdfPreview
        isOpen={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        pdfUrl={pdfUrl}
        boardName={boardData?.name || ''}
      />

      {/* Notes Drawer */}
      <BottomSheet
        open={notesDrawerOpen}
        onOpenChange={setNotesDrawerOpen}
        title="Board Notes"
        maxHeight="max-h-[85vh]"
      >
        <div className="flex flex-col" style={{ minHeight: '60vh' }}>
          <div className="flex-1 mb-4">
            <FormLabel>
              Add notes for this board
            </FormLabel>
            <Textarea
              value={boardNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Enter notes about this board..."
              className="text-xs min-h-[400px] resize-y border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              style={{ height: 'auto' }}
            />
          </div>
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleCloseNotesDrawer}
              variant="outline"
              size="sm"
            >
              Close
            </Button>
            <Button
              onClick={handleSaveNotes}
              variant="primary"
              size="sm"
            >
              Save Notes
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#141821] border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="max-w-[99vw] mx-auto flex items-center gap-3">
          <Button
            onClick={handleOpenNotesDrawer}
            variant="outline"
            size="sm"
            className="inline-flex items-center gap-2"
          >
            <Paperclip className="w-4 h-4" />
            Notes
            {boardNotes && (
              <span className="ml-1 w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></span>
            )}
          </Button>
          <div className="ml-auto">
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              className="border border-[#3778E2] bg-[#141821] text-[#3778E2] shadow-sm hover:bg-[#1a2332] hover:border-[#3778E2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-9 px-4 py-2"
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Circuit Row Component
const CircuitRow = ({ index, circuit, onChange, onDelete, zsFactor, onOpenContextMenu, inputClassName, onOpenNotesModal, selectedColumn, boardData, isCompleted }) => {
  const rowClass = circuit.selected
    ? 'bg-blue-900/30'
    : '';

  const getCellClass = (field) => {
    const baseClass = 'border border-gray-300 dark:border-gray-700 px-2 py-2 bg-[#141821]';
    if (selectedColumn === field) {
      return `${baseClass} bg-blue-900/40`;
    }
    return baseClass;
  };

  return (
    <tr className={`hover:bg-[#1a2332] ${rowClass}`}>
      <td className={getCellClass('selected')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'description')}>
        <input
          type="checkbox"
          checked={!!circuit.selected}
          onChange={(e) => onChange(circuit.id, 'selected', e.target.checked)}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('displayNumber')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'displayNumber')}>
        <input
          type="text"
          value={circuit.displayNumber || ''}
          onChange={(e) => onChange(circuit.id, 'displayNumber', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'displayNumber')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('description')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'numPointsServed')}>
        <input
          type="text"
          value={circuit.description}
          onChange={(e) => onChange(circuit.id, 'description', e.target.value)}
          placeholder="e.g., Lights"
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'description')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('numPointsServed')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'wiringType')}>
        <input
          type="text"
          value={circuit.numPointsServed}
          onChange={(e) => onChange(circuit.id, 'numPointsServed', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'numPointsServed')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('wiringType')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'refMethod')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.wiringType}
            disabled={isCompleted}
            onChange={(val) => {
              const raw = (val || '').toString().trim();
              const upper = raw.toUpperCase();
              let display = raw;
              if (/^[A-Z]\s*\(.*\)/i.test(raw)) {
                display = raw.charAt(0).toUpperCase();
              } else if (upper === 'N/A' || upper === 'LIM') {
                display = upper;
              }
              onChange(circuit.id, 'wiringType', display);
            }}
            options={[
              'A (PVC/PVC Cables)',
              'B (PVC Cables in metalic conduit)',
              'C (PVC Cables in non-metalic conduit core)',
              'D (PVC Cables in metalic trunking)',
              'E (PVC Cables in non-metalic trunking)',
              'F (PVC/SWA Cables)',
              'G (XLPE/SWA Cables)',
              'O (Mineral Insulated Cables)',
              'N/A',
              'LIM',
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'wiringType')}
          />
        </div>
      </td>
      <td className={getCellClass('refMethod')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'live')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.refMethod}
            disabled={isCompleted}
            onChange={(val) => {
              const raw = (val || '').toString().trim();
              let display = raw;
              if (raw.includes('(')) {
                display = raw.split('(')[0].trim();
              }
              display = display.toUpperCase();
              onChange(circuit.id, 'refMethod', display);
            }}
            options={[
              'A (In conduit/trunking in thermally insulating wall)',
              'B (Enclosed in conduit on surface)',
              'C (Clipped direct to surface)',
              'D (On cable tray or ladder)',
              'E (In free air)',
              'F (Buried direct in ground)',
              'G (Buried in duct in ground)',
              '100 (Enclosed in conduit on a wall or embedded in plaster)',
              '101 (T&E cable clipped directly to wooden joist or above ceiling with >100 mm insulation)',
              '102 (Enclosed in conduit/trunking on a wall, but not embedded)',
              '103 (Cable in conduit/trunking embedded in an insulated wall)',
              '104 (Cable in conduit/trunking surrounded by thermal insulation)',
              '105 (Cable in conduit/trunking embedded in masonry wall)',
              '106 (Multicore cable enclosed in conduit on wall)',
              '101A/101B (T&E cable on joist – touching or spaced)',
              'C1/C2 (Clipped direct, grouped)'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'refMethod')}
          />
        </div>
      </td>
      <td className={getCellClass('live')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'cpc')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.live}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'live', (val || '').toString())}
            options={[
              '0.5','1','1.5','2.5','4','6','10','16','25','35','50','70'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'live')}
          />
        </div>
      </td>
      <td className={getCellClass('cpc')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.cpc}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'cpc', (val || '').toString())}
            options={[
              '0.5','1','1.5','2.5','4','6','10','16','25','35','50','70'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'cpc')}
          />
        </div>
      </td>
      <td className={`${getCellClass('maxDisconnectTime')}`} style={{ position: 'relative', overflow: 'visible' }} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'maxDisconnectTime')}>
        <ComboInput
          value={circuit.maxDisconnectTime}
          disabled={isCompleted}
          onChange={(value) => onChange(circuit.id, 'maxDisconnectTime', value)}
          options={['0.2', '0.4', '1', '5']}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'maxDisconnectTime')}
        />
      </td>
      <td className={getCellClass('bsEn')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'bsEn')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.bsEn}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'bsEn', (val || '').toString())}
            options={[
              '60898','61009','61009/62606 AFDD','60947-2','60947-3','88-2','88-3','1361','1362','3036','3871','4752','5419','5486','50439','62423','62606','MCB','MCCB','ACB','N/V','N/A','LIM'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'bsEn')}
          />
        </div>
      </td>
      <td className={getCellClass('type')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'type')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.type}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'type', (val || '').toString())}
            options={[
              'B','C','D','K','Z','1','2','3','gG','gM','aM','S1A','S2A','S4A','N/A','LIM'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'type')}
          />
        </div>
      </td>
      <td className={getCellClass('rating')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rating')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.rating}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'rating', (val || '').toString())}
            options={[
              '5','6','10','15','16','20','25','32','40','50','63','80','100','N/A'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rating')}
          />
        </div>
      </td>
      <td className={getCellClass('breakingCapacity')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'breakingCapacity')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.breakingCapacity}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'breakingCapacity', (val || '').toString())}
            options={[
              '6','10','15','25','33','36'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'breakingCapacity')}
          />
        </div>
      </td>
      <td className={getCellClass('maxZs')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'maxZs')}>
        <div className="flex items-center gap-2">
        <input
            type="text"
            value={circuit.maxZs}
            onChange={(e) => onChange(circuit.id, 'maxZs', e.target.value)}
            className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'maxZs')}
          disabled={isCompleted}
          />
          <button
            type="button"
            onClick={() => {
              if (isCompleted) return;
              const std = normalizeBsEn(circuit.bsEn);
              if (!std) {
                alert('Max Zs calculation works only for BS 60898 / 61009 / 61009/62606 AFDD with valid Type and Rating.');
                return;
              }
              const base = computeMaxZsBase({
                time: circuit.maxDisconnectTime,
                bsEn: circuit.bsEn,
                type: circuit.type,
                rating: circuit.rating,
              });
              if (base === '') {
                alert('Max Zs calculation requires valid Time, BS(EN), Type and Rating for supported standards.');
                return;
              }
              const scaled = applyZsFactor(base, zsFactor);
              if (scaled) onChange(circuit.id, 'maxZs', scaled);
            }}
            className="inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title="Calculate Max Zs"
          >
            <Calculator className="w-4 h-4" />
          </button>
        </div>
      </td>
      <td className={getCellClass('rcdBsEn')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdBsEn')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.rcdBsEn}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'rcdBsEn', (val || '').toString())}
            options={[
              '61008','61009','62606','4293','1363','7071','7288','LIM','N/A'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdBsEn')}
          />
        </div>
      </td>
      <td className={getCellClass('rcdType')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdType')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.rcdType}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'rcdType', (val || '').toString())}
            options={[
              'A','AC','B','F','K','Z','HP','EV','AKV'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdType')}
          />
        </div>
      </td>
      <td className={getCellClass('rcdOperatingCurrent')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdOperatingCurrent')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.rcdOperatingCurrent}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'rcdOperatingCurrent', (val || '').toString())}
            options={[
              '6','10','30','50','100','200','300','500','1000','N/AC'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdOperatingCurrent')}
          />
        </div>
      </td>
      <td className={getCellClass('rcdRating')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdRating')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.rcdRating}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'rcdRating', (val || '').toString())}
            options={[
              '5','6','10','15','16','20','25','32','40','50','63','80','100','N/A'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdRating')}
          />
        </div>
      </td>
      <td className={getCellClass('r1Live')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r1Live')}>
        <input
          type="text"
          value={circuit.r1Live}
          onChange={(e) => onChange(circuit.id, 'r1Live', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r1Live')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('mNeutral')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'mNeutral')}>
        <input
          type="text"
          value={circuit.mNeutral}
          onChange={(e) => onChange(circuit.id, 'mNeutral', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'mNeutral')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('r2Cpc')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r2Cpc')}>
        <input
          type="text"
          value={circuit.r2Cpc}
          onChange={(e) => onChange(circuit.id, 'r2Cpc', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r2Cpc')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('r1PlusR2')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r1PlusR2')}>
        <input
          type="text"
          value={circuit.r1PlusR2}
            onChange={(e) => onChange(circuit.id, 'r1PlusR2', e.target.value)}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r1PlusR2')}
            disabled={isCompleted}
          />
      </td>
      <td className={getCellClass('r2')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r2')}>
        <input
          type="text"
          value={circuit.r2}
          onChange={(e) => onChange(circuit.id, 'r2', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'r2')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('testVoltage')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'testVoltage')}>
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ComboInput
            value={circuit.testVoltage}
            disabled={isCompleted}
            onChange={(val) => onChange(circuit.id, 'testVoltage', (val || '').toString())}
            options={[
              '100','250','500','1000'
            ]}
            className={inputClassName}
            onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'testVoltage')}
          />
        </div>
      </td>
      <td className={getCellClass('ll')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'll')}>
        <input
          type="text"
          value={circuit.ll}
          onChange={(e) => onChange(circuit.id, 'll', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'll')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('le')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'le')}>
        <input
          type="text"
          value={circuit.le}
          onChange={(e) => onChange(circuit.id, 'le', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'le')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('polarity')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'polarity')}>
        <div className="flex items-center justify-center">
          {circuit.polarity === 'PASS' ? (
            <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
          ) : circuit.polarity === 'FAIL' ? (
            <span className="text-red-600 dark:text-red-400 text-lg">✗</span>
          ) : (
            <div style={{ position: 'relative', overflow: 'visible' }}>
              <ComboInput
                value={circuit.polarity}
                disabled={isCompleted}
                onChange={(val) => onChange(circuit.id, 'polarity', (val || '').toString())}
                options={[
                  'PASS','FAIL','LIM','N/A'
                ]}
                className={inputClassName}
                onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'polarity')}
              />
            </div>
          )}
        </div>
      </td>
      <td className={getCellClass('measuredZs')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'measuredZs')}>
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2">
            <input
              type="text"
              value={circuit.measuredZs}
              onChange={(e) => onChange(circuit.id, 'measuredZs', e.target.value)}
              disabled={isCompleted}
              className={`${inputClassName} ${(() => {
                const measured = parseFloat(circuit.measuredZs);
                const maxZs = parseFloat(circuit.maxZs);
                if (!isNaN(measured) && !isNaN(maxZs) && measured > maxZs) {
                  return 'text-red-600 dark:text-red-400';
                }
                return '';
              })()}`}
              onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'measuredZs')}
            />
            {(() => {
              const measured = parseFloat(circuit.measuredZs);
              const maxZs = parseFloat(circuit.maxZs);
              if (!isNaN(measured) && !isNaN(maxZs) && measured > maxZs) {
                return (
                  <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                );
              }
              return null;
            })()}
          </div>
          <button
            type="button"
            onClick={() => {
              if (isCompleted) return;
              // Calculate Measured Zs = R1+R2 + Zs at DB
              const r1PlusR2Str = (circuit.r1PlusR2 || '').toString().trim();
              const zsAtDbStr = (boardData?.zsAtDb || '').toString().trim();
              
              if (!r1PlusR2Str || !zsAtDbStr) {
                alert('Please ensure both R1+R2 and Zs at DB have values.');
                return;
              }
              
              const r1PlusR2 = parseFloat(r1PlusR2Str);
              const zsAtDb = parseFloat(zsAtDbStr);
              
              if (isNaN(r1PlusR2) || isNaN(zsAtDb)) {
                alert('Please ensure both R1+R2 and Zs at DB have valid numeric values.');
                return;
              }
              
              const result = r1PlusR2 + zsAtDb;
              // Round to 2 decimal places
              const roundedResult = parseFloat(result.toFixed(2));
              onChange(circuit.id, 'measuredZs', roundedResult.toString());
            }}
            className="inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title="Calculate Measured Zs (R1+R2 + Zs at DB)"
          >
            <Calculator className="w-4 h-4" />
          </button>
        </div>
      </td>
      <td className={getCellClass('rcdDisconTime')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdDisconTime')}>
        <input
          type="text"
          value={circuit.rcdDisconTime}
          onChange={(e) => onChange(circuit.id, 'rcdDisconTime', e.target.value)}
          className={inputClassName}
          onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdDisconTime')}
          disabled={isCompleted}
        />
      </td>
      <td className={getCellClass('rcdTestButton')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdTestButton')}>
        <div className="flex items-center justify-center">
          {circuit.rcdTestButton === 'PASS' ? (
            <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
          ) : circuit.rcdTestButton === 'FAIL' ? (
            <span className="text-red-600 dark:text-red-400 text-lg">✗</span>
          ) : (
            <div style={{ position: 'relative', overflow: 'visible' }}>
              <ComboInput
                value={circuit.rcdTestButton}
                disabled={isCompleted}
                onChange={(val) => onChange(circuit.id, 'rcdTestButton', (val || '').toString())}
                options={[
                  'PASS','FAIL','LIM','N/A'
                ]}
                className={inputClassName}
                onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'rcdTestButton')}
              />
            </div>
          )}
        </div>
      </td>
      <td className={getCellClass('afddTestButton')} onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'afddTestButton')}>
        <div className="flex items-center justify-center">
          {circuit.afddTestButton === 'PASS' ? (
            <span className="text-green-600 dark:text-green-400 text-lg">✓</span>
          ) : circuit.afddTestButton === 'FAIL' ? (
            <span className="text-red-600 dark:text-red-400 text-lg">✗</span>
          ) : (
            <div style={{ position: 'relative', overflow: 'visible' }}>
              <ComboInput
                value={circuit.afddTestButton}
                disabled={isCompleted}
                onChange={(val) => onChange(circuit.id, 'afddTestButton', (val || '').toString())}
                options={[
                  'PASS','FAIL','LIM','N/A'
                ]}
                className={inputClassName}
                onContextMenu={(e) => onOpenContextMenu(e, circuit.id, 'afddTestButton')}
              />
            </div>
          )}
        </div>
      </td>
      <td className={getCellClass('notes')}>
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => onOpenNotesModal(circuit.id)}
            className={`relative inline-flex items-center justify-center p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
              circuit.notes ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
            }`}
            title={circuit.notes ? 'View/Edit Notes' : 'Add Notes'}
          >
            <FileText className="w-4 h-4" />
            {circuit.notes && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-white dark:border-gray-800"></span>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
};

export default CircuitsManager;

